export const CONTACT_LIMITS = {
  name: 80,
  email: 254,
  subject: 120,
  message: 2000,
} as const;

export type ContactFormFields = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

export type ContactFieldName = keyof ContactFormFields;

export type ContactFieldErrors = Partial<Record<ContactFieldName, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateName = (value: string): string | undefined => {
  const name = value.trim();
  if (!name) return "Name is required.";
  if (name.length > CONTACT_LIMITS.name) {
    return `Name must be ${CONTACT_LIMITS.name} characters or fewer.`;
  }
  return undefined;
};

const validateEmail = (value: string): string | undefined => {
  const email = value.trim();
  if (!email) return "Email is required.";
  if (email.length > CONTACT_LIMITS.email) {
    return `Email must be ${CONTACT_LIMITS.email} characters or fewer.`;
  }
  if (!EMAIL_RE.test(email)) return "Enter a valid email address.";
  return undefined;
};

const validateSubject = (value: string): string | undefined => {
  const subject = value.trim();
  if (!subject) return "Subject is required.";
  if (subject.length > CONTACT_LIMITS.subject) {
    return `Subject must be ${CONTACT_LIMITS.subject} characters or fewer.`;
  }
  return undefined;
};

const validateMessage = (value: string): string | undefined => {
  const message = value.trim();
  if (!message) return "Message is required.";
  if (message.length > CONTACT_LIMITS.message) {
    return `Message must be ${CONTACT_LIMITS.message} characters or fewer.`;
  }
  return undefined;
};

const FIELD_VALIDATORS: Record<
  ContactFieldName,
  (value: string) => string | undefined
> = {
  name: validateName,
  email: validateEmail,
  subject: validateSubject,
  message: validateMessage,
};

export const validateContactField = (
  field: ContactFieldName,
  value: string
): string | undefined => FIELD_VALIDATORS[field](value);

export const validateContactForm = (
  values: ContactFormFields
): ContactFieldErrors => {
  const errors: ContactFieldErrors = {};

  (Object.keys(FIELD_VALIDATORS) as ContactFieldName[]).forEach((field) => {
    const message = validateContactField(field, values[field]);
    if (message) errors[field] = message;
  });

  return errors;
};

export const hasContactFieldErrors = (errors: ContactFieldErrors) =>
  Object.keys(errors).length > 0;
